/**
 * Iter 45i / FP3-054 — Numbers Everywhere (scoped diagnostic mode)
 *
 * Super-admin-only count-integrity dashboard. Reads `/api/admin/numbers`
 * with `as_org` / `as_school` from the sidebar context selector and
 * renders:
 *   - context pill + summary cards
 *   - status / provider / fidelity / hidden-data breakdown sections
 *   - mismatch warnings at the top
 *   - existing schoolwide cross-surface matrix (scope-aware)
 *   - provider-scoping verification (existing, scope-aware)
 *   - Copy diagnostic JSON button (counts only, no PHI)
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import {
  Check,
  Warning,
  Info,
  ArrowsClockwise,
  MagicWand,
  Buildings,
  GraduationCap,
  Copy as CopyIcon,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSuperAdminContext } from "@/lib/useSuperAdminContext";
import { localDateStr } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUS_META = {
  match: { label: "Match", tone: "ok", Icon: Check },
  mismatch: { label: "Mismatch", tone: "danger", Icon: Warning },
  intentional_difference: { label: "Intentional", tone: "info", Icon: Info },
  derived: { label: "Derived", tone: "warn", Icon: Info },
  unavailable: { label: "Unavailable", tone: "muted", Icon: Info },
  not_comparable: { label: "Not comparable", tone: "muted", Icon: Info },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unavailable;
  const tone = {
    ok: "bg-[#E8F3EE] text-[#1E5B3F] border-[#A8D5BA]",
    danger: "bg-[#FCE8E6] text-[#A4221C] border-[#F4B7B2]",
    info: "bg-[#EAF1F8] text-[#1F4E8C] border-[#B7D0EA]",
    warn: "bg-[#FCF1DE] text-[#8C5F00] border-[#EFD49A]",
    muted: "bg-[#F1F2F4] text-[#6B7280] border-[#D9DCE0]",
  }[meta.tone];
  const Icon = meta.Icon;
  return (
    <span
      data-testid={`numbers-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${tone}`}
    >
      <Icon size={11} weight="bold" />
      {meta.label}
    </span>
  );
}

/**
 * FP3-015 — Read-only Suspicious Sessions tile.
 *
 * Calls /api/admin/diagnostics/suspicious-sessions?today=<local YYYY-MM-DD>
 * and surfaces the count + breakdown on Numbers Everywhere. NEVER mutates
 * data; if the endpoint fails, the rest of Numbers Everywhere keeps
 * rendering and the tile shows "Diagnostic unavailable".
 *
 * Total count source-of-truth:
 *   The endpoint already returns counts.{future_completed,
 *   future_with_notes, future_with_evidence, future_with_minutes,
 *   future_recurring_completed, total_future}. A single suspicious row
 *   may carry several flags, so summing the categories would
 *   double-count. We use `sessions.length` (distinct flagged rows
 *   returned by the diagnostic) as the total and surface the
 *   per-category counts as the breakdown without re-summing them.
 */
const SUSPICIOUS_BREAKDOWN = [
  { key: "future_completed", label: "Future completed" },
  { key: "future_with_notes", label: "Future with notes" },
  { key: "future_with_evidence", label: "Future with evidence" },
  { key: "future_with_minutes", label: "Future with minutes" },
  { key: "future_recurring_completed", label: "Future recurring completed" },
];

function SuspiciousSessionsTile() {
  const [state, setState] = useState({ status: "loading", data: null });

  useEffect(() => {
    let cancelled = false;
    const today = localDateStr();
    api
      .getSuspiciousSessions({ today })
      .then((res) => {
        if (cancelled) return;
        setState({ status: "ok", data: res.data, today });
      })
      .catch((err) => {
        if (cancelled) return;
        // Calm degraded state — log normally, don't crash the page.
        console.error("Suspicious sessions diagnostic unavailable:", err);
        setState({ status: "error", data: null, today });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Distinct flagged rows. Prefer the diagnostic's own row count over
  // re-summing categories (one row may carry several flags → summing
  // would double-count). See module docblock above.
  const total = useMemo(() => {
    if (state.status !== "ok" || !state.data) return 0;
    const sessions = state.data.sessions;
    return Array.isArray(sessions) ? sessions.length : 0;
  }, [state]);

  const counts = state.data?.counts || {};
  const isError = state.status === "error";
  const isLoading = state.status === "loading";

  // Tone matches the calm muted Numbers Everywhere palette; no red.
  return (
    <Card data-testid="suspicious-sessions-tile" className="border-[#E5E7EB]">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] text-[#6B7280] uppercase tracking-wider mb-1">
              <Info size={12} weight="bold" /> Data integrity · Read-only
            </div>
            <h3
              data-testid="suspicious-sessions-heading"
              className="text-sm font-semibold text-[#111827]"
            >
              Suspicious sessions
            </h3>
            <p
              data-testid="suspicious-sessions-supporting"
              className="text-[12px] text-[#6B7280] mt-1 max-w-[520px] leading-snug"
            >
              {isError
                ? "Diagnostic unavailable. The rest of Numbers Everywhere is unaffected."
                : isLoading
                ? "Checking for future-dated session records that should not exist…"
                : total === 0
                ? "No suspicious future session records found."
                : "Review recommended. No data has been changed."}
            </p>
          </div>

          <div className="text-right shrink-0">
            <div
              data-testid="suspicious-sessions-count"
              className={`text-3xl font-semibold tabular-nums ${
                isError ? "text-[#9CA3AF]" : "text-[#111827]"
              }`}
            >
              {isError ? "—" : isLoading ? "·" : total}
            </div>
            {!isError && state.today && (
              <div className="text-[11px] text-[#6B7280] mt-1">
                as of {state.today}
              </div>
            )}
          </div>
        </div>

        {state.status === "ok" && total > 0 && (
          <div
            data-testid="suspicious-sessions-breakdown"
            className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 pt-4 border-t border-[#F1F2F4]"
          >
            {SUSPICIOUS_BREAKDOWN.map(({ key, label }) => (
              <div
                key={key}
                data-testid={`suspicious-sessions-breakdown-${key.replace(/_/g, "-")}`}
                className="px-3 py-2 rounded-md bg-[#FAFBFC] border border-[#F1F2F4]"
              >
                <div className="text-[11px] text-[#6B7280] leading-tight">{label}</div>
                <div className="text-[15px] font-semibold tabular-nums text-[#111827] mt-0.5">
                  {counts[key] ?? 0}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OverallBanner({ overall }) {
  const map = {
    match: { tone: "bg-[#E8F3EE] border-[#A8D5BA] text-[#1E5B3F]", text: "All counting surfaces agree." },
    match_with_intentional_diffs: {
      tone: "bg-[#E8F3EE] border-[#A8D5BA] text-[#1E5B3F]",
      text: "All counting surfaces agree on canonical concepts. Intentional differences are documented below.",
    },
    mismatch: { tone: "bg-[#FCE8E6] border-[#F4B7B2] text-[#A4221C]", text: "Drift detected — see rows flagged below." },
    incomplete: { tone: "bg-[#FCF1DE] border-[#EFD49A] text-[#8C5F00]", text: "Some surfaces did not return data." },
  }[overall] || {
    tone: "bg-[#F1F2F4] border-[#D9DCE0] text-[#6B7280]",
    text: "No data yet.",
  };
  return (
    <div data-testid="numbers-overall-banner" className={`px-4 py-3 rounded-lg border ${map.tone}`}>
      <div className="text-sm font-semibold uppercase tracking-wide mb-0.5">Health</div>
      <div className="text-sm">{map.text}</div>
    </div>
  );
}

// FP3-054 — Small generic two-column breakdown card used by the
// Status / Provider / Service Fidelity sections. Renders a label →
// count list with an optional extra block (e.g. by_archive_reason)
// and an optional footer note.
function BreakdownCard({ testid, title, rows, extra, footer }) {
  return (
    <Card data-testid={testid} className="border-[#E5E7EB]">
      <CardContent className="p-0">
        <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFBFC]">
          <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
        </div>
        <ul className="divide-y divide-[#F1F2F4]">
          {rows.map(([label, value], i) => (
            <li
              key={i}
              data-testid={`${testid}-row-${i}`}
              className="px-4 py-2 flex items-baseline justify-between"
            >
              <span className="text-[12.5px] text-[#374151]">{label}</span>
              <span className="text-[14px] font-semibold tabular-nums text-[#111827] ml-2">
                {value ?? "—"}
              </span>
            </li>
          ))}
        </ul>
        {extra}
        {footer && (
          <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#FAFBFC] text-[11.5px] text-[#6B7280] italic">
            {footer}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MatrixTable({ rows, title }) {
  // Group rows by concept so each concept's cross-surface comparisons
  // sit together visually.
  const groups = useMemo(() => {
    const out = new Map();
    for (const r of rows) {
      if (!out.has(r.concept)) out.set(r.concept, []);
      out.get(r.concept).push(r);
    }
    return Array.from(out.entries());
  }, [rows]);

  return (
    <Card className="border-[#E5E7EB]">
      <CardContent className="p-0">
        {title && (
          <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFBFC]">
            <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-[#FAFBFC] border-b border-[#E5E7EB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="text-left px-4 py-2 font-medium">Concept · Scope</th>
                <th className="text-right px-3 py-2 font-medium">Canonical</th>
                <th className="text-right px-3 py-2 font-medium">Surface</th>
                <th className="text-left px-4 py-2 font-medium">Surface name</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(([concept, group]) =>
                group.map((r, idx) => (
                  <tr
                    key={`${concept}-${idx}`}
                    data-testid={`numbers-row-${concept.replace(/\s+/g, "-").toLowerCase()}-${idx}`}
                    className={`border-b border-[#F1F2F4] last:border-0 ${
                      r.status === "mismatch" ? "bg-[#FCF7F6]" : "hover:bg-[#FAFBFC]"
                    }`}
                  >
                    <td className="px-4 py-2.5 align-top">
                      {idx === 0 && (
                        <div className="font-medium text-[#111827] mb-0.5">{concept}</div>
                      )}
                      <div className="text-[11px] text-[#6B7280] leading-snug">{r.scope}</div>
                      {r.note && (
                        <div className="text-[11px] text-[#6B7280] mt-1 italic leading-snug max-w-[600px]">
                          {r.note}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-[#111827]">
                      {r.canonical ?? "—"}
                    </td>
                    <td
                      className={`px-3 py-2.5 text-right tabular-nums font-semibold ${
                        r.status === "mismatch" ? "text-[#A4221C]" : "text-[#111827]"
                      }`}
                    >
                      {r.surface_value ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-[#374151] font-mono text-[11px] leading-snug">
                      {r.surface}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusPill status={r.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminNumbers() {
  // FP3-043 Phase A follow-up — Numbers Everywhere is a Super Admin
  // / platform-diagnostic tool only. Non-super-admin callers get
  // redirected to /admin so the URL never shows the page (the
  // backend also returns 403 — this is defense in depth).
  const { user, loading: authLoading } = useAuth();
  // FP3-054 — Scope follows the sidebar context selector via URL.
  const [searchParams] = useSearchParams();
  const asOrg = (searchParams.get("as_org") || "").trim();
  const asSchool = (searchParams.get("as_school") || "").trim();
  const { orgName: ctxOrgName, schoolName: ctxSchoolName } = useSuperAdminContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [error, setError] = useState(null);

  const load = async (pid = providerId) => {
    setLoading(true);
    try {
      const params = {};
      if (pid) params.provider_id = pid;
      if (asOrg) params.as_org = asOrg;
      if (asSchool) params.as_school = asSchool;
      const res = await api.getAdminNumbers(params);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load numbers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (user?.permission_role !== "super_admin") return;
    api.getProviders().then((r) => setProviders(r.data)).catch(() => {});
    load(""); // initial scoped load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.permission_role]);

  useEffect(() => {
    if (authLoading) return;
    if (user?.permission_role !== "super_admin") return;
    load(providerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, asOrg, asSchool, authLoading, user?.permission_role]);

  const summary = useMemo(() => {
    if (!data) return null;
    const counts = { match: 0, mismatch: 0, intentional_difference: 0, derived: 0, unavailable: 0 };
    for (const r of data.rows) {
      counts[r.status] = (counts[r.status] || 0) + 1;
    }
    return counts;
  }, [data]);

  const contextLabel = (() => {
    if (data?.scope?.label) return data.scope.label;
    if (ctxOrgName && ctxSchoolName) return `${ctxOrgName} · ${ctxSchoolName}`;
    if (ctxOrgName) return ctxOrgName;
    return "All Organizations";
  })();

  const handleCopyJson = async () => {
    if (!data) return;
    // FP3-054 — Counts-only payload. We strip `provider_section.provider.name`
    // (the one place a human name appears) before copying so the
    // diagnostic remains paste-safe in any channel.
    const safe = { ...data };
    if (safe.provider_section) {
      safe.provider_section = {
        ...safe.provider_section,
        provider: {
          id: safe.provider_section.provider?.id || "",
          role: safe.provider_section.provider?.role || "",
        },
      };
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(safe, null, 2));
      toast.success("Diagnostic JSON copied (counts only · no PHI)");
    } catch {
      toast.error("Copy failed");
    }
  };

  // FP3-043 Phase A follow-up — Block non-super-admin entirely.
  // We render a redirect AFTER the hooks above so React's rules-of-
  // hooks invariant is preserved (every render hits the same number
  // of hooks).
  if (authLoading) return null;
  if (user?.permission_role !== "super_admin") {
    return (
      <Navigate
        to="/admin"
        replace
        state={{ flash: "Numbers Everywhere is restricted to platform owners." }}
      />
    );
  }

  return (
    <div data-testid="admin-numbers-page" className="p-6 max-w-[1400px] mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[11px] text-[#6B7280] uppercase tracking-wider mb-1">
            <MagicWand size={12} weight="bold" /> Internal · Count integrity
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-semibold text-[#111827]">Numbers Everywhere</h1>
            {/* FP3-054 — Compact context pill mirrors AdminShell's
                pill so the operator always sees the active scope. */}
            <span
              data-testid="numbers-context-pill"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#E6F1F8] border border-[#1F4F73]/30 text-[11.5px] text-[#0F3554]"
            >
              {asSchool ? (
                <GraduationCap size={12} weight="duotone" className="text-[#1F4F73]" />
              ) : (
                <Buildings size={12} weight="duotone" className="text-[#1F4F73]" />
              )}
              <span className="font-medium truncate max-w-[36ch]">
                Viewing: {contextLabel}
              </span>
            </span>
          </div>
          <p className="text-sm text-[#6B7280] mt-1 max-w-[680px]">
            Scoped count-integrity check across every counting surface. Source of truth is{" "}
            <code className="font-mono text-[12px] bg-[#F1F2F4] px-1 py-0.5 rounded">student_cohorts.partition</code>.
            Use this to verify after deploys or debug count discrepancies for a specific organization / campus.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="numbers-copy-json-btn"
            variant="outline"
            size="sm"
            onClick={handleCopyJson}
            disabled={loading || !data}
            title="Copy the structured diagnostic payload (counts only · no PHI)"
          >
            <CopyIcon size={14} weight="bold" className="mr-1.5" />
            Copy diagnostic JSON
          </Button>
          <Button
            data-testid="numbers-refresh-btn"
            variant="outline"
            size="sm"
            onClick={() => load(providerId)}
            disabled={loading}
          >
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin mr-1.5" : "mr-1.5"} />
            Refresh
          </Button>
        </div>
      </div>

      {data && <OverallBanner overall={data.overall} />}

      {/* FP3-054 — Mismatch warnings strip. Surfaces every row whose
          surface_value disagrees with the canonical cohort count
          for the active scope. Renders only when there are warnings. */}
      {data?.warnings && data.warnings.length > 0 && (
        <Card
          data-testid="numbers-warnings"
          className="border-[#F4B7B2] bg-[#FCF7F6]"
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <Warning size={18} weight="duotone" className="text-[#A4221C] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-[#A4221C] mb-1">
                  {data.warnings.length} count mismatch
                  {data.warnings.length === 1 ? "" : "es"} in the current scope
                </h3>
                <ul className="text-[12.5px] text-[#7A3A2C] space-y-1">
                  {data.warnings.slice(0, 6).map((w, i) => (
                    <li
                      key={i}
                      data-testid={`numbers-warning-${i}`}
                      className="leading-snug"
                    >
                      <strong>{w.concept}</strong> · expected{" "}
                      <span className="tabular-nums font-semibold">
                        {w.expected ?? "—"}
                      </span>{" "}
                      from canonical, surface{" "}
                      <span className="font-mono text-[11px]">{w.surface}</span>{" "}
                      returned{" "}
                      <span className="tabular-nums font-semibold">
                        {w.got ?? "—"}
                      </span>
                    </li>
                  ))}
                  {data.warnings.length > 6 && (
                    <li className="italic text-[#6B7280]">
                      … and {data.warnings.length - 6} more (see matrix below)
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* FP3-054 — Summary cards (scoped counts).
          Counts only; no PHI; safe for any super-admin viewer. */}
      {data?.summary && (
        <Card
          data-testid="numbers-summary"
          className="border-[#E5E7EB]"
        >
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFBFC]">
              <h3 className="text-sm font-semibold text-[#111827]">Summary counts · scoped</h3>
              <p className="text-[11.5px] text-[#6B7280] mt-0.5">
                Every value below reflects <strong>{contextLabel}</strong>.
                When you switch context in the sidebar, this section
                re-fetches.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-[#F1F2F4]">
              {[
                ["organizations", "Organizations"],
                ["schools", "Schools / campuses"],
                ["students_total", "Students total"],
                ["students_active", "Students active"],
                ["students_archived", "Students archived"],
                ["providers_total", "Providers"],
                ["users_total", "Users"],
                ["admins_total", "Admins"],
                ["pending_invites", "Pending invites"],
                ["pending_access_requests", "Pending access requests"],
                ["goals_active", "Goals (active)"],
                ["sessions_count", "Sessions"],
              ].map(([key, label]) => (
                <div
                  key={key}
                  data-testid={`numbers-summary-${key}`}
                  className="bg-white px-3 py-2.5"
                >
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                    {label}
                  </div>
                  <div className="text-[18px] font-semibold tabular-nums text-[#111827] mt-0.5">
                    {data.summary[key] ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* FP3-054 — Status / Provider / Service Fidelity breakdowns. */}
      {data?.status_breakdown && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BreakdownCard
            testid="numbers-status-breakdown"
            title="Student status"
            rows={[
              ["Active", data.status_breakdown.active],
              ["Archived", data.status_breakdown.archived],
              ["Exited last 30 days", data.status_breakdown.exited_last_30d],
              ["Missing school link", data.status_breakdown.missing_school_link],
              ["Missing provider assignment", data.status_breakdown.missing_provider_assignment],
              ["Missing external ID", data.status_breakdown.missing_external_id],
            ]}
            extra={
              Object.keys(data.status_breakdown.by_archive_reason || {}).length > 0 && (
                <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#FAFBFC]">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-1.5">
                    By archive reason
                  </div>
                  <ul className="space-y-1 text-[12px] text-[#374151]">
                    {Object.entries(data.status_breakdown.by_archive_reason).map(([reason, count]) => (
                      <li key={reason} className="flex items-baseline justify-between">
                        <span className="truncate">{reason}</span>
                        <span className="tabular-nums font-semibold ml-2">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }
          />
          <BreakdownCard
            testid="numbers-provider-breakdown"
            title="Providers & team"
            rows={[
              ["Total providers", data.provider_breakdown.total],
              ["With caseload", data.provider_breakdown.with_caseload],
              ["With zero caseload", data.provider_breakdown.with_zero_caseload],
              ["Students with no provider", data.provider_breakdown.students_with_no_provider],
              ["Students with multiple providers", data.provider_breakdown.students_with_multiple_providers],
            ]}
            extra={
              Object.keys(data.provider_breakdown.by_role || {}).length > 0 && (
                <div className="px-4 py-2.5 border-t border-[#E5E7EB] bg-[#FAFBFC]">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold mb-1.5">
                    By role
                  </div>
                  <ul className="space-y-1 text-[12px] text-[#374151]">
                    {Object.entries(data.provider_breakdown.by_role).map(([role, count]) => (
                      <li key={role} className="flex items-baseline justify-between">
                        <span className="truncate font-mono text-[11px]">{role}</span>
                        <span className="tabular-nums font-semibold ml-2">{count}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            }
          />
          <BreakdownCard
            testid="numbers-fidelity-breakdown"
            title="Service fidelity"
            rows={[
              ["Students with commitments", data.service_fidelity_breakdown.students_with_commitments],
              ["Students without commitments", data.service_fidelity_breakdown.students_without_commitments],
            ]}
            footer={data.service_fidelity_breakdown.fidelity_rows_note}
          />
        </div>
      )}

      {/* FP3-054 — Hidden data diagnostic. Surfaces the show-hidden
          toggle state plus the counts that are being suppressed in
          the operational view. */}
      {data?.hidden_data_summary && (
        <Card data-testid="numbers-hidden-data" className="border-[#E5E7EB]">
          <CardContent className="p-0">
            <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFBFC] flex items-baseline justify-between gap-3 flex-wrap">
              <h3 className="text-sm font-semibold text-[#111827]">Demo / test / hidden data</h3>
              <div className="text-[11.5px] text-[#6B7280]">
                <span className="font-mono">Show demo/test</span>{" "}
                {data.hidden_data_summary.show_hidden_toggle ? "ON" : "OFF"}
                {data.hidden_data_summary.include_hidden_query && (
                  <span> · query override ON</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[#F1F2F4]">
              {[
                ["Visible students", data.hidden_data_summary.students_visible],
                ["Hidden students", data.hidden_data_summary.students_hidden],
                ["Archived-test students", data.hidden_data_summary.archived_test_count],
                ["Orphan students", data.hidden_data_summary.orphan_needs_review_count],
                ["Hidden pending invites", data.hidden_data_summary.invites_pending_hidden],
                ["Hidden pending requests", data.hidden_data_summary.access_requests_pending_hidden],
              ].map(([label, value], i) => (
                <div
                  key={i}
                  data-testid={`numbers-hidden-${i}`}
                  className="bg-white px-3 py-2.5"
                >
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold">
                    {label}
                  </div>
                  <div className="text-[18px] font-semibold tabular-nums text-[#111827] mt-0.5">
                    {value ?? "—"}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {summary && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge data-testid="numbers-count-match" className="bg-[#E8F3EE] text-[#1E5B3F] border border-[#A8D5BA]">
            {summary.match} match
          </Badge>
          {summary.mismatch > 0 && (
            <Badge data-testid="numbers-count-mismatch" className="bg-[#FCE8E6] text-[#A4221C] border border-[#F4B7B2]">
              {summary.mismatch} mismatch{summary.mismatch === 1 ? "" : "es"}
            </Badge>
          )}
          {summary.intentional_difference > 0 && (
            <Badge
              data-testid="numbers-count-intentional"
              className="bg-[#EAF1F8] text-[#1F4E8C] border border-[#B7D0EA]"
            >
              {summary.intentional_difference} intentional
            </Badge>
          )}
          {summary.derived > 0 && (
            <Badge data-testid="numbers-count-derived" className="bg-[#FCF1DE] text-[#8C5F00] border border-[#EFD49A]">
              {summary.derived} derived
            </Badge>
          )}
          {summary.unavailable > 0 && (
            <Badge className="bg-[#F1F2F4] text-[#6B7280] border border-[#D9DCE0]">
              {summary.unavailable} unavailable
            </Badge>
          )}
          {data?.as_of && (
            <span className="text-[11px] text-[#6B7280] ml-2">
              as of {data.as_of}
            </span>
          )}
        </div>
      )}

      {error && (
        <div className="px-4 py-3 rounded-lg border bg-[#FCE8E6] border-[#F4B7B2] text-[#A4221C] text-sm">
          {error}
        </div>
      )}

      {loading && !data && <div className="text-sm text-[#6B7280]">Loading…</div>}

      {/* FP3-015 — Read-only data-integrity tile. Independent of the main
          /api/admin/numbers fetch so a diagnostic outage cannot regress
          the rest of Numbers Everywhere. */}
      <SuspiciousSessionsTile />

      {data && <MatrixTable rows={data.rows} title="Schoolwide concepts" />}

      {/* ── Provider-scoping verification ─────────────────────────────── */}
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-0">
          <div className="px-5 py-3 border-b border-[#E5E7EB] bg-[#FAFBFC] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-[#111827]">Provider-scoping verification</h3>
              <p className="text-[12px] text-[#6B7280] mt-0.5">
                Confirms a provider's caseload matches across the Caseload page, Admin Reports, and{" "}
                <code className="font-mono text-[11px]">/admin/providers</code>. This is the surface for catching the bug
                class that previously had Sarah's Caseload showing 18 vs Admin Reports showing 13.
              </p>
            </div>
            <select
              data-testid="numbers-provider-select"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              className="text-sm border border-[#D1D5DB] rounded-md px-2 py-1.5 bg-white"
            >
              <option value="">— Choose a provider —</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.first_name} {p.last_name} · {p.role}
                </option>
              ))}
            </select>
          </div>

          {!providerId && (
            <div className="px-5 py-6 text-sm text-[#6B7280] italic">
              Pick a provider above to verify their counts agree across surfaces.
            </div>
          )}

          {providerId && data?.provider_section && (
            <>
              <div className="px-5 py-3 text-[12px] text-[#6B7280] border-b border-[#E5E7EB]">
                Verifying:{" "}
                <span className="font-semibold text-[#111827]">{data.provider_section.provider.name}</span> ·{" "}
                <span className="font-mono">{data.provider_section.provider.role}</span>
                {data.provider_section.extra?.archived_students_ever_assigned > 0 && (
                  <span className="ml-2 text-[#6B7280]">
                    · {data.provider_section.extra.archived_students_ever_assigned} archived student
                    {data.provider_section.extra.archived_students_ever_assigned === 1 ? "" : "s"} ever assigned
                  </span>
                )}
              </div>
              <MatrixTable rows={data.provider_section.rows} />
            </>
          )}
        </CardContent>
      </Card>

      <div className="text-[11px] text-[#6B7280] italic pt-2">
        This page calls every counting surface in-process and compares each return value against{" "}
        <code className="font-mono">student_cohorts.partition</code>. It does not introduce a new counting engine.
      </div>
    </div>
  );
}
